import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SummarizeIcon from '@mui/icons-material/Summarize';

import PDFReport from '../MapView/modals/PDFReport';
import useReportData from '../../hooks/useReportData';
import api from '../../services/api';
import { classificationApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const EMPTY_VEHICLES = [];

const buildVehicleScope = (filters, availableVehicles) => {
  const hasVehicleScope = Object.prototype.hasOwnProperty.call(filters, 'selectAllVehicles') ||
    Array.isArray(filters.deviceUuids);
  if (!hasVehicleScope) return null;

  const isAllVehicles = filters.selectAllVehicles !== false;
  const selectedUuidSet = new Set(filters.deviceUuids || []);
  const matchedVehicles = isAllVehicles
    ? []
    : availableVehicles.filter(vehicle => selectedUuidSet.has(vehicle.device_uuid));
  const matchedUuidSet = new Set(matchedVehicles.map(vehicle => vehicle.device_uuid));
  const missingVehicles = isAllVehicles
    ? []
    : (filters.deviceUuids || [])
      .filter(deviceUuid => !matchedUuidSet.has(deviceUuid))
      .map(deviceUuid => ({
        device_uuid: deviceUuid,
        device_name: 'Unknown vehicle',
        vehicle_id: deviceUuid.substring(0, 8),
        is_active: null
      }));

  return {
    mode: isAllVehicles ? 'all' : 'specific',
    totalRegisteredVehicles: availableVehicles.length,
    vehicles: [...matchedVehicles, ...missingVehicles].map(vehicle => ({
      deviceUuid: vehicle.device_uuid,
      deviceName: vehicle.device_name || 'Vehicle',
      vehicleId: vehicle.vehicle_id || vehicle.device_uuid?.substring(0, 8) || 'N/A',
      isActive: vehicle.is_active
    }))
  };
};

/**
 * Modal that builds data based on the same filters from ReportsPage and renders PDFReport for preview.
 * This is a first-iteration MVP: we only fetch validated defects and do not yet attach photos.
 */
const ReportPreviewModal = ({
  open,
  onClose,
  filters, // Includes network, section, date, defect type, and optional vehicle scope filters.
  availableVehicles = EMPTY_VEHICLES,
  onReportSaved
}) => {
  const theme = useTheme();
  const { loading, error, defectReport } = useReportData(filters, open);
  const downloadRef = useRef();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0, phase: '' });
  const { user } = useAuth();

  // State for report summary data
  const [reportSummary, setReportSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Fetch summary data when filters change
  useEffect(() => {
    const fetchSummaryData = async () => {
      if (!filters) {
        setReportSummary(null);
        setLoadingSummary(false);
        return;
      }

      if (filters.isEmpty) {
        const vehicleScope = buildVehicleScope(filters, availableVehicles);
        setReportSummary(vehicleScope ? {
          network: { name: 'N/A' },
          sectionsMode: 'all',
          selectedSections: [],
          startDate: filters.startDate,
          endDate: filters.endDate,
          selectAllDefects: true,
          selectedDefectTypes: [],
          totalDefectTypes: 0,
          generatedBy: user?.fullName || 'Unknown User',
          includeRepeatedFindings: false,
          onlyDefectsWithRepeated: false,
          vehicleScope
        } : null);
        setLoadingSummary(false);
        return;
      }

      setLoadingSummary(true);
      try {
        const [networksResponse, defectTypesResponse] = await Promise.all([
          api.get('/networks?limit=1000'),
          classificationApi.getDefectTypes({ asset_type_id: 1 })
        ]);

        const networks = networksResponse.data || [];
        const defectTypes = defectTypesResponse.data || [];

        // Find selected network
        const selectedNetwork = networks.find(n => n.id === parseInt(filters.networkId));

        // Get sections if specific sections are selected
        let selectedSections = [];
        if (filters.sectionsMode === 'specific' && filters.sectionIds && filters.sectionIds.length > 0) {
          try {
            const sectionsResponse = await api.get(`/networks/${filters.networkId}/sections`);
            const allSections = sectionsResponse.data || [];
            selectedSections = allSections.filter(s => filters.sectionIds.includes(s.id));
          } catch (err) {
            console.error('Error fetching sections:', err);
          }
        }

        // Get selected defect types
        const selectedDefectTypes = filters.selectAllDefects 
          ? defectTypes 
          : defectTypes.filter(dt => filters.defectTypeIds.includes(dt.id));

        // Vehicle scope is opt-in so legacy and non-road report consumers do
        // not gain a new PDF field unless they provide the vehicle filters.
        const vehicleScope = buildVehicleScope(filters, availableVehicles);

        setReportSummary({
          network: selectedNetwork,
          sectionsMode: filters.sectionsMode,
          selectedSections,
          startDate: filters.startDate,
          endDate: filters.endDate,
          selectAllDefects: filters.selectAllDefects,
          selectedDefectTypes,
          totalDefectTypes: defectTypes.length,
          generatedBy: user?.fullName || 'Unknown User',
          includeRepeatedFindings: !!filters.includeRepeatedFindings,
          onlyDefectsWithRepeated: !!filters.onlyDefectsWithRepeated,
          vehicleScope
        });
      } catch (error) {
        console.error('Error fetching summary data:', error);
        setReportSummary(null);
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchSummaryData();
  }, [filters, availableVehicles, user?.fullName]);

  // Build pseudo-inspection object used by PDFReport
  const inspectionStub = {
    title: 'IRIS Report Preview',
    date: new Date().toISOString(),
    id: 'preview'
  };

  // Handle PDF download with database save
  const handleDownloadPDF = async () => {
    if (!downloadRef.current || !defectReport || loadingSummary) return;

    try {
      setUploadingReport(true);
      
      // Generate PDF without auto-download first
      const pdfData = await downloadRef.current.generatePDF(false);
      if (!pdfData) {
        throw new Error('Failed to generate PDF');
      }

      // Prepare report metadata
      const reportName = `Road Inspection Report - ${new Date().toLocaleDateString()}`;
      const description = `Road inspection report containing ${defectReport.data.total} validated defects`;
      
      // Build report parameters from filters
      const parameters = {
        filters,
        generatedAt: new Date().toISOString(),
        defectCount: defectReport.data.total,
        photoCount: defectReport.data.photosWithDefectsCount || 0,
        ...(reportSummary?.vehicleScope ? { vehicleScope: reportSummary.vehicleScope } : {})
      };

      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('pdfFile', pdfData.pdfBlob, pdfData.filename);
      formData.append('reportName', reportName);
      formData.append('reportType', 'envis_defect_report');
      formData.append('description', description);
      formData.append('parameters', JSON.stringify(parameters));
      
      if (filters.startDate) {
        formData.append('startDate', filters.startDate.toISOString().split('T')[0]);
      }
      if (filters.endDate) {
        formData.append('endDate', filters.endDate.toISOString().split('T')[0]);
      }

      // Upload to backend
      const response = await api.post('/reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        if (onReportSaved) {
          onReportSaved(); // Notify parent to refresh list
        }
        // Trigger download for user
        const url = URL.createObjectURL(pdfData.pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdfData.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log('Report saved successfully:', response.data);
      } else {
        throw new Error(response.data.error || 'Failed to save report');
      }

    } catch (error) {
      console.error('Error downloading and saving report:', error);
      // Fallback: try to download anyway
      if (downloadRef.current) {
        downloadRef.current.handleDownload();
      }
    } finally {
      setUploadingReport(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 2
        }}
      >
        Report Preview
      </DialogTitle>
      <DialogContent
        sx={{ p: 0, position: 'relative', bgcolor: theme.palette.mode === 'dark' ? '#111' : '#fafafa' }}
      >
        {loading && (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
        )}
        {!loading && !error && defectReport && (
          <PDFReport
            inspection={inspectionStub}
            defectReport={defectReport}
            photos={defectReport?.data?.photosWithDefects || []}
            severityLevels={[]}
            hideDownloadButton={true}
            onDownloadRef={downloadRef}
            onLoadingChange={setIsGeneratingPDF}
            onProgressChange={setPdfProgress}
            reportSummary={reportSummary}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        {/* Progress text display */}
        {(isGeneratingPDF || uploadingReport) && pdfProgress.phase === 'Rendering' && (
          <Box sx={{ flex: 1, pl: 1 }}>
            <span style={{ fontSize: '0.875rem', color: '#666' }}>
              Capturing page {pdfProgress.current}/{pdfProgress.total}
            </span>
          </Box>
        )}
        <Button onClick={onClose} color="inherit">Close</Button>
        {/* PDFReport already has its own download button, but extra convenience */}
        <Button
          variant="contained"
          startIcon={(isGeneratingPDF || uploadingReport || loadingSummary) ? <CircularProgress size={20} color="inherit" /> : <SummarizeIcon />}
          onClick={handleDownloadPDF}
          disabled={!defectReport || isGeneratingPDF || uploadingReport || loadingSummary}
        >
          {loadingSummary ? 'Loading Summary...' : isGeneratingPDF ? 'Generating PDF...' : uploadingReport ? 'Saving Report...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportPreviewModal;
